/* eslint-disable no-extend-native */
/**
 * @module msysCommon
 * @description
 * msysCommon is general utility library.  It does the following:
 * - adds methods to LODASH (third-party general library)
 *   available automatically for all sources using _. prefix;
 *   methods include date/time formatting (yyyymmdd, hhmmss, etc.),
 *   number formatting (natural, two, thousands),
 *   and string formatting (joinStrings, stringToArgList, …)
 * - adds promisify method to Function prototype
 * - adds few methods to String prototype (reverse)
 * - adds few methods to ArrayBuffer prototype (from, fromString, fromArray, toHex, …)
 * - adds few methods to Date prototype (hms, ymd,, …)
 * - implements JSON.stringifySafe class method to stringify anything
 *   including objects with circular references (therefore no need to try/catch block
 *   like in case of standard JSON.stringify)
 * - implements global asyncTimeout function to perform async-based setTimeout
 */

const _ = module.exports = require('lodash')

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ENHANCE LODASH
if(_) {
  _.CONSTANTS = {
    Soitron: 'u2s1yh4mkbre',
    Toluca: '9g3jsq2cqen34',
    MexicoCity: '9g3qrfx5h',
    Warszaw: 'u3qcnhhk3yy',
    Skopje: 'srrnztn6n1z0',
    Paris: 'u09tvrt8rut8',
    Sala_Kralovska: 'u2s9yts09kn2',
    Viscofan: 'u2dq9dgy6',
    Bukurest: 'sxfscbgd7',
    Prague: 'u2fkc41f7p1c',
    Kishinev: 'u8ke2wtny7q1',
  }
}

if(_ && _.mixin) {
  _.mixin({

    // converts anything to natural number
    natural: function(num) {
      num = parseInt(num) || 0
      return num<0 ? 0 : num
    },

    ifNaN: function(...vals) {
      for(let val of vals) {
        val = parseFloat(val)
        if(!isNaN(val)) return val
      }
      return 0
    },

    ifEmpty: function(...vals) {
      for(const val of vals) {
        if(val!==undefined && val!==null && val!=='') return val
      }
    },

    // converts number to at least 2-digits
    two: function(str) {
      str = parseInt(str) || 0
      return (str>=0 && str<10 ? '0'+str : str)
    },

    yyyymmdd: function(d) {
      d = d instanceof Date ? d : String(parseInt(d))===String(d) ? new Date(parseInt(d)) : d ? new Date(d) : new Date()
      return(
        String(d.getFullYear()).padStart(4, 0)+
        String(d.getMonth()+1).padStart(2, 0)+
        String(d.getDate()).padStart(2, 0)
      )
    }, // yyyymmdd

    hhmmss: function(d) {
      d = d instanceof Date ? d : String(parseInt(d))===String(d) ? new Date(parseInt(d)) : d ? new Date(d) : new Date()
      return(
        String(d.getHours()).padStart(2, 0)+
        String(d.getMinutes()).padStart(2, 0)+
        String(d.getSeconds()).padStart(2, 0)
      )
    }, // hhmmss

    hhmm: function(d) {
      d = d instanceof Date ? d : String(parseInt(d))===String(d) ? new Date(parseInt(d)) : d ? new Date(d) : new Date()
      return(
        String(d.getHours()).padStart(2, 0)+
        String(d.getMinutes()).padStart(2, 0)
      )
    }, // hhmm

    ymdhms: function(d) {
      d = d instanceof Date ? d : String(parseInt(d))===String(d) ? new Date(parseInt(d)) : d ? new Date(d) : new Date()
      return _.yyyymmdd(d)+_.hhmmss(d)
    }, // ymdhms

    debugdate: function(d) {
      d = !d ? new Date() : _.isObjectLike(d) ? d : (String(parseInt(d))===String(d)) ? new Date(parseInt(d)) : new Date(d)
      return(
        String(d.getFullYear()).padStart(4, '0') + '-' +
        String(d.getMonth()+1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0')
      )
    }, // debugdate

    debugtime: function(d) {
      d = !d ? new Date() : _.isObjectLike(d) ? d : (String(parseInt(d))===String(d)) ? new Date(parseInt(d)) : new Date(d)
      return(
        String(d.getHours()).padStart(2, '0') + ':' +
        String(d.getMinutes()).padStart(2, '0') + ':' +
        String(d.getSeconds()).padStart(2, '0') + '.' +
        String(d.getMilliseconds()).padStart(3, '0')
      )
    }, // debugtime

    debugdatetime: function(d) {
      d = !d ? new Date() : _.isObjectLike(d) ? d : (String(parseInt(d))===String(d)) ? new Date(parseInt(d)) : new Date(d)
      return(
        String(d.getFullYear()).padStart(4, '0') + '-' +
        String(d.getMonth()+1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0') + ' ' +
        String(d.getHours()).padStart(2, '0') + ':' +
        String(d.getMinutes()).padStart(2, '0') + ':' +
        String(d.getSeconds()).padStart(2, '0') + '.' +
        String(d.getMilliseconds()).padStart(3, '0')
      )
    }, // debugdatetime

    userdatetime: function(d) {
      d = !d ? new Date() : _.isObjectLike(d) ? d : (String(parseInt(d))===String(d)) ? new Date(parseInt(d)) : new Date(d)
      return(
        String(d.getFullYear()).padStart(4, '0') + '-' +
        String(d.getMonth()+1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0') + ' ' +
        String(d.getHours()).padStart(2, '0') + ':' +
        String(d.getMinutes()).padStart(2, '0') + ':' +
        String(d.getSeconds()).padStart(2, '0')
      )
    }, // userdatetime

    usertime: function(d) {
      d = !d ? new Date() : _.isObjectLike(d) ? d : (String(parseInt(d))===String(d)) ? new Date(parseInt(d)) : new Date(d)
      return(
        String(d.getHours()).padStart(2, '0') + ':' +
        String(d.getMinutes()).padStart(2, '0') + ':' +
        String(d.getSeconds()).padStart(2, '0')
      )
    }, // usertime

    us2dbdate: function(datetime_us) {
      // transform epoch timestamp in microseconds (or seconds) to database string representation including enclosing ''
      datetime_us = parseInt(datetime_us)
      const usec = String(datetime_us % 1_000).padStart(3, '0')
      const datetime_ms = Math.floor(datetime_us / 1_000)
      const dt = new Date(datetime_ms)
      let str = dt.toISOString()
      str = str.replace(/Z$/, usec+'Z')
      return "'"+str+"'"
    },

    epochdayToString: function(dt) {
      dt = parseInt(dt)
      if(!dt) return '-'
      dt = new Date(dt*86400000)
      return(dt.getDate()+'.'+(1+dt.getMonth())+'.'+dt.getFullYear())
    },

    usec2dt: function(usec, milis = true) {
      if(!(usec>0)) return '-'
      const d = new Date(Math.floor(usec/1000))
      const ms = milis ? '.'+ String(d.getUTCMilliseconds()).padStart(3, 0) : ''
      return(
        String(d.getUTCFullYear()).padStart(4, '0') + '-' +
        String(d.getUTCMonth()+1).padStart(2, '0') + '-' +
        String(d.getUTCDate()).padStart(2, '0') + ' ' +
        String(d.getUTCHours()).padStart(2, 0)+':'+
        String(d.getUTCMinutes()).padStart(2, 0)+':'+
        String(d.getUTCSeconds()).padStart(2, 0)+ ms
      )
    },

    usec2ymdhmsms: function(usec, milis = true) {
      if(!(usec>0)) return '-'
      const d = new Date(Math.floor(usec/1000))
      const ms = milis ? '.'+ String(d.getMilliseconds()).padStart(3, 0) : ''
      return(
        String(d.getFullYear()).padStart(4, '0') + '-' +
        String(d.getMonth()+1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0') + ' ' +
        String(d.getHours()).padStart(2, 0)+':'+
        String(d.getMinutes()).padStart(2, 0)+':'+
        String(d.getSeconds()).padStart(2, 0)+ ms
      )
    },

    usec2ymdhmsus: function(usec) {
      if(!(usec>0)) return '-'
      const d = new Date(Math.floor(usec/1000))
      return(
        String(d.getFullYear()).padStart(4, '0') + '-' +
        String(d.getMonth()+1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0') + ' ' +
        String(d.getHours()).padStart(2, 0)+':'+
        String(d.getMinutes()).padStart(2, 0)+':'+
        String(d.getSeconds()).padStart(2, 0)+'.'+
        String(usec % 1_000_000).padStart(6, 0)
      )
    },

    usec2dtus: function(usec) {
      if(!(usec>0)) return '-'
      const d = new Date(Math.floor(usec/1000))
      return(
        String(d.getUTCFullYear()).padStart(4, '0') + '-' +
        String(d.getUTCMonth()+1).padStart(2, '0') + '-' +
        String(d.getUTCDate()).padStart(2, '0') + ' ' +
        String(d.getUTCHours()).padStart(2, 0)+':'+
        String(d.getUTCMinutes()).padStart(2, 0)+':'+
        String(d.getUTCSeconds()).padStart(2, 0)+'.'+
        String(usec % 1_000_000).padStart(6, 0)
      )
    },

    usec2d: function(usec) {
      if(!(usec>0)) return '-'
      const d = new Date(Math.floor(usec/1000))
      return(
        String(d.getUTCFullYear()).padStart(4, '0') + '-' +
        String(d.getUTCMonth()+1).padStart(2, '0') + '-' +
        String(d.getUTCDate()).padStart(2, '0')
      )
    },

    usec2t: function(usec, milis = true) {
      if(!(usec>0)) return '-'
      const d = new Date(Math.floor(usec/1000))
      const ms = milis ? '.'+ String(d.getUTCMilliseconds()).padStart(3, 0) : ''
      return(
        String(d.getUTCHours()).padStart(2, 0)+':'+
        String(d.getUTCMinutes()).padStart(2, 0)+':'+
        String(d.getUTCSeconds()).padStart(2, 0) + ms
      )
    },

    usec2lt: function(usec, milis = true) {
      if (!(usec > 0)) return '-'
      const d = new Date(Math.floor(usec / 1000))
      const ms = milis ? '.' + String(d.getMilliseconds()).padStart(3, '0') : ''
      return (
        String(d.getHours()).padStart(2, '0') + ':' +
        String(d.getMinutes()).padStart(2, '0') + ':' +
        String(d.getSeconds()).padStart(2, '0') + ms
      )
    },

    usec2ld: function(usec) {
      if (!(usec > 0)) return '-'
      const d = new Date(Math.floor(usec / 1000))
      return (
        String(d.getFullYear()).padStart(4, '0') + '-' +
        String(d.getMonth() + 1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0')
      )
    },

    usec2ldt: function(usec, milis = true) {
      if (!(usec > 0)) return '-'
      const d = new Date(Math.floor(usec / 1000))
      const ms = milis ? '.' + String(d.getMilliseconds()).padStart(3, '0') : ''
      return (
        String(d.getFullYear()).padStart(4, '0') + '-' +
        String(d.getMonth() + 1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0') + ' ' +
        String(d.getHours()).padStart(2, '0') + ':' +
        String(d.getMinutes()).padStart(2, '0') + ':' +
        String(d.getSeconds()).padStart(2, '0') + ms
      )
    },

    usec2tus: function(usec) {
      if(!(usec>0)) return '-'
      const d = new Date(Math.floor(usec/1000))
      return(
        String(d.getUTCHours()).padStart(2, 0)+':'+
        String(d.getUTCMinutes()).padStart(2, 0)+':'+
        String(d.getUTCSeconds()).padStart(2, 0)+'.'+
        String(usec % 1_000_000).padStart(6, 0)
      )
    },

    usec2duration: function(usec) {
      if(!(usec>0 || usec<0)) return '-'
      const minus = usec<0
      const msec = Math.floor(Math.abs(usec)/1000)
      const h = Math.floor(msec/3_600_000)
      const m = Math.floor((msec%3_600_000)/60_000)
      const s = Math.floor((msec%60_000)/1_000)
      const ms = msec%1_000
      return(
        (minus ? '-' : '') +
        (h>0?h+':':'') +
        String(m).padStart(2, 0)+':'+
        String(s).padStart(2, 0)+'.'+
        String(ms).padStart(3, 0)
      )
    },

    thousands: function(x) {
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },

    arrayToHash: function(arr) {
      if(!Array.isArray(arr)) return {}
      return Object.fromEntries(arr.map(value=>[value, 1]))
    },

    joinStrings: function() {
      const separator = String(arguments[0] ?? '')
      const parts = []
      for(let i=1; i<arguments.length; i++) {
        const str = String(arguments[i] ?? '')
        if(str!=='') parts.push(str)
      }
      return parts.join(separator)
    },

    splitTextHalf: function(text) {
      const words = text.trim().split(/\s+/)
      const totalLength = text.length
      const line1 = []
      const line2 = []
      let len1 = 0

      for(const word of words) {
        if((len1 + word.length + line1.length) <= totalLength / 2 || line1.length === 0) {
          line1.push(word)
          len1 += word.length
        }else {
          line2.push(word)
        }
      }

      return [line1.join(' '), line2.join(' ')]
    },

    stringToArgList: function(str) {
      // eslint-disable-next-line no-control-regex
      return String(str??'').replace(/""/g, '\x01').replace(/"([^"]+)"/g, (m, m1)=>m1.replace(/ /g, '\x02')).replace(/ +/g, '\x03').replace(/\x01/g, '"').replace(/\x02/g, ' ').split(/\x03/)
    },

    colorFromFirstCharacter: function(str) {
      const char = String(str??'').substr(0, 1).toLowerCase()
      return(
        char==='g' ? 'green' :
          char==='o' ? 'orange' :
            char==='r' ? 'red' :
              char==='b' ? 'blue' :
                char==='w' ? 'white' :
                  'black'
      )
    },

    formatWithDate: function(str, d, additions) {
      str = String(str??'')
      if(str==='') return ''
      d = !d ? new Date() : _.isObjectLike(d) ? d : String(parseInt(d))===String(d) ? new Date(parseInt(d)) : new Date(d)

      // append date-related default
      const templates = _.defaults(additions, {
        e: d.valueOf(),
        y: d.getFullYear(),
        m: ('0' + (d.getMonth() + 1)).slice(-2),
        d: ('0' + d.getDate()).slice(-2),
        H: ('0' + d.getHours()).slice(-2),
        M: ('0' + d.getMinutes()).slice(-2),
        S: ('0' + d.getSeconds()).slice(-2),
        s: ('00' + d.getMilliseconds()).slice(-3),
      })
      return str.replace(/%(%|\w)/g, (m, m1) =>
        m1==='%' ? '%' : String(templates[m1]??''),
      )
    },

    base64toBlob(base64Data, contentType, sliceSize) {
      contentType = contentType || ''
      sliceSize = sliceSize || 1024
      const byteCharacters = atob(base64Data)
      const byteArrays = []
      for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize)
        const byteNumbers = new Array(slice.length)
        for(let i=0; i<slice.length; i++) byteNumbers[i] = slice.charCodeAt(i)
        const byteArray = new Uint8Array(byteNumbers)
        byteArrays.push(byteArray)
      }
      return new Blob(byteArrays, { type: contentType })
    },

    memorySize(object) {
      const objectList = []
      const stack = [object]
      let bytes = 0

      while(stack.length) {
        const value = stack.pop()
        if(typeof value === 'boolean') {
          bytes += 4
        }else if (typeof value === 'string') {
          bytes += value.length * 2 // 2 bytes per character for strings
        }else if (typeof value === 'number') {
          bytes += 8 // 64-bit floating-point numbers
        }else if (typeof value === 'object' && value !== null && !objectList.includes(value)) {
          objectList.push(value)
          for(const key in value) {
            if(Object.prototype.hasOwnProperty.call(value, key)) {
              bytes += key.length * 2 // Keys are strings, 2 bytes per character
              stack.push(value[key]) // Recursively calculate for properties
            }
          }
        }
      }
      return bytes
    },

    roundBy(n, decimals) {
      n = parseFloat(n)
      if(isNaN(n)) return n
      if(!(decimals>0)) return Math.round(n)
      return Number(n.toFixed(decimals))
    },

    callers(idx) {
      const err = new Error()
      const lines = err.stack.split('\n')
      let callers = lines.splice(2)
      while(callers.length>0 && /node:internal/.test(callers[callers.length-1])) {
        callers.pop()
      }
      callers = callers.map(line => {
        const m = /^\s*at\s+(\S+)/.exec(line)
        return m ? m[1] : ''
      })

      callers = callers.filter(line => !!line)

      if(!isNaN(idx) && idx>=0) {
        return callers[idx] ?? ''
      }else {
        return callers
      }
    },
  })
} // END OF LODASH ENAHNCE

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * helper function for perfomance logging
 */
if(typeof window === 'object') {
  let perf_dt = window.performance.now()
  window.perfReset = function() {
    perf_dt = window.performance.now()
  }
  window.perf = function() {
    const dt = performance.now()
    if(dt-perf_dt>8) {
      Array.prototype.unshift.call(arguments, 'PERF', (dt-perf_dt).toFixed(3))
      console.error.apply(null, arguments)
    }else if(dt-perf_dt>2) {
      Array.prototype.unshift.call(arguments, 'PERF', (dt-perf_dt).toFixed(3))
      console.warn.apply(null, arguments)
    }else{
      Array.prototype.unshift.call(arguments, 'PERF', (dt-perf_dt).toFixed(3))
      console.log.apply(null, arguments)
    }
    perf_dt = dt
  }
  window._perfSummaryCount = {}
  window._perfSummaryTime = {}
  window.perfAdd = function(key) {
    const dt = performance.now()
    window._perfSummaryCount[key] = (window._perfSummaryCount[key] || 0) + 1
    window._perfSummaryTime[key] = (window._perfSummaryTime[key] || 0) + dt-perf_dt
    perf_dt = dt
  }
  window.perfSummary = function() {
    console.log('PERF SUMMARY\n'+Object.keys(window._perfSummaryTime).sort((a, b)=>window._perfSummaryTime[b]-window._perfSummaryTime[a]).map(t=>`${t}: ${window._perfSummaryCount[t]} ~ ${window._perfSummaryTime[t]}ms`).join('\n'))
  }
}

Function.prototype.promisify = (manyArgs = false) => {
  return (...args) => {
    return new Promise((resolve, reject) => {
      function callback(err, ...results) {
        if(err) return reject(err)
        else return resolve(manyArgs ? results : results[0])
      }
      args.push(callback)
      this.call(this, ...args)
    })
  }
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * implemented missing reverse() on string
 */
String.prototype.reverse = function() {
  const s = this
  let o = ''
  for (let i = s.length - 1; i >= 0; i--) { o += s[i] }
  return o
}

/**
 * Searches a string from left to right for a pattern and returns a substring consisting of the characters in the string that are to the left of the pattern.
 * @param pattern
 * @returns {string}
 */
String.prototype.strLeft= function(pattern) {
  pattern = String(pattern ?? '')
  if(pattern==='') return ''
  return (this.indexOf(pattern) === -1 || pattern==='') ? '' : this.split(pattern)[0]
}

ArrayBuffer.from = function(src) {
  if(typeof src==='string') return ArrayBuffer.fromString(src)
  if(typeof Buffer==='function' && src instanceof Buffer) return src.buffer.slice(src.byteOffset, src.byteOffset+src.length)
  if(src instanceof ArrayBuffer) return src
  if(src && typeof src==='object' && src.buffer instanceof ArrayBuffer) return src.buffer
  if(Array.isArray(src)) return ArrayBuffer.fromArray(src)
} // from

ArrayBuffer.fromString = function(str, limit) {
  if(typeof str!=='string') return
  let ab
  if(typeof TextEncoder === 'undefined') {
    try{ ab = ArrayBuffer.from(Buffer.from(str)) }catch(e) {}
  }else{
    try{ ab = new TextEncoder('utf-8').encode(str).buffer }catch(e) {}
  }
  if(ab && ab.byteLength>limit) ab = ab.slice(0, limit)
  return ab
} // fromString

ArrayBuffer.fromArray = function(arr) {
  if(!Array.isArray(arr)) return
  return new Uint8Array(arr).buffer
} // fromArray

ArrayBuffer.concat = function() {
  let total = 0
  const args = []
  for(let i=0; i<arguments.length; i++) {
    const arg = ArrayBuffer.from(arguments[i])
    if(arg) {
      args.push(new Uint8Array(arg))
      total += arg.byteLength
    }
  }
  const out = new Uint8Array(total)
  let offset = 0
  for(let i=0; i<args.length; i++) {
    out.set(new Uint8Array(args[i]), offset)
    offset += args[i].byteLength
  }
  return out.buffer
} // concat

ArrayBuffer.prototype.toHex = function(limit) {
  let uint = new Uint8Array(this)
  if(limit>0) uint = uint.slice(0, limit)
  return Array.prototype.map.call(uint, x => (x.toString(16).padStart(2, '0'))+' ').join('').replace(/ $/, '').toUpperCase()
} // toHex

ArrayBuffer.prototype.toBit = function(limit) {
  let uint = new Uint8Array(this)
  if(limit>0) uint = uint.slice(0, limit)
  return Array.prototype.map.call(uint, x => (x.toString(2).padStart(8, '0'))+' ').join('').replace(/ $/, '').toUpperCase()
} // toBit

ArrayBuffer.prototype.toString = function(limit) {
  let uint = new Uint8Array(this)
  if(limit>0) uint = uint.slice(0, limit)
  if(typeof TextEncoder === 'undefined') {
    try{ return Buffer.from(uint).toString() }catch(e) {}
  }else{
    try{ return new TextDecoder('utf-8').decode(uint) }catch(e) {}
  }
} // toString

ArrayBuffer.prototype.dumpHex = function(...args) {
  console.log(...args, this.toHex())
} // dumpHex

ArrayBuffer.prototype.dumpString = function(...args) {
  console.log(...args, this.toString())
} // dumpString

Error.prototype.toJSON = function() {
  const alt = {}
  for(const p of Object.getOwnPropertyNames(this)) { alt[p] = this[p] }
  return alt
} // Error.prototype.toJSON

Uint8Array.prototype.equals = function(uint) {
  return(
    (uint instanceof Uint8Array) &&
    this.byteLength === uint.byteLength &&
    this.every((val, i)=>val===uint[i])
  )
}

Date.prototype.hmsms = function() {
  if(isNaN(this.valueOf())) return ''
  return(
    String(this.getUTCHours()).padStart(2, 0)+':'+
    String(this.getUTCMinutes()).padStart(2, 0)+':'+
    String(this.getUTCSeconds()).padStart(2, 0)+'.'+
    String(this.getUTCMilliseconds()).padStart(3, 0)
  )
}

Date.prototype.hms = function() {
  if(isNaN(this.valueOf())) return ''
  return(
    String(this.getUTCHours()).padStart(2, 0)+':'+
    String(this.getUTCMinutes()).padStart(2, 0)+':'+
    String(this.getUTCSeconds()).padStart(2, 0)
  )
}

Date.prototype.ymd = function() {
  if(isNaN(this.valueOf())) return ''
  return(
    this.getUTCFullYear()+'-'+
    String(this.getUTCMonth()+1).padStart(2, 0)+'-'+
    String(this.getUTCDate()).padStart(2, 0)
  )
}

Date.prototype.ymdhms = function() {
  if(isNaN(this.valueOf())) return ''
  return(
    this.getUTCFullYear()+'-'+
    String(this.getUTCMonth()+1).padStart(2, 0)+'-'+
    String(this.getUTCDate()).padStart(2, 0) + ' '+
    String(this.getUTCHours()).padStart(2, 0)+':'+
    String(this.getUTCMinutes()).padStart(2, 0)+':'+
    String(this.getUTCSeconds()).padStart(2, 0)
  )
}

Date.prototype.ymdhmsms = function() {
  if(isNaN(this.valueOf())) return ''
  return(
    this.getUTCFullYear()+'-'+
    String(this.getUTCMonth()+1).padStart(2, 0)+'-'+
    String(this.getUTCDate()).padStart(2, 0) + ' '+
    String(this.getUTCHours()).padStart(2, 0)+':'+
    String(this.getUTCMinutes()).padStart(2, 0)+':'+
    String(this.getUTCSeconds()).padStart(2, 0)+'.'+
    String(this.getUTCMilliseconds()).padStart(3, 0)
  )
}

JSON.stringifySafe = (obj, indent) => {
  try{
    const str = JSON.stringify(obj, null, indent)
    return str
  }catch(e) {}

  try{
    if(obj && obj instanceof Error) {
      const str = JSON.stringify(obj, Object.getOwnPropertyNames(obj), indent)
      return str
    }
  }catch(e) {}

  try{
    const seen = new WeakSet()
    return JSON.stringify(obj, (key, val) => {
      if(typeof val === 'object' && val !== null) {
        if(seen.has(val)) return
        seen.add(val)
      }
      return val
    }, indent)
  }catch(e) {
    return ''
  }
} // JSON.stringifySafe

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * @description
 * safe JSON stringification with property exclusion
 * @param {any} obj - variable to be serialised
 * @param {function|RegExp} exclude - excludes object properties matching RegExp or if function(key,val) return true
 * @param {null|string} indent - JSON.stringify indentation
 */
JSON.stringifyExcluding = (obj, exclude, indent) => {
  const seen = new WeakSet()
  return JSON.stringify(obj, (key, val) => {
    if(exclude instanceof RegExp && exclude.test(key)) return
    if(typeof exclude==='function' && exclude(key, val)) return
    if(typeof val === 'object' && val !== null) {
      if(seen.has(val)) return
      seen.add(val)
    }
    return val
  }, indent)
}

(typeof window==='object' ? window : global).asyncTimeout = (delay) => new Promise(resolve=>setTimeout(resolve, delay))
